import { Button, Checkbox, FloatingLabel, Select } from '@/components';
import { getLocalizedDataset } from '@/lib/dataset';
import { getCampaignData } from '@/lib/helpers';
import { sendGTMEvent } from '@next/third-parties/google';
import axios from 'axios';
import { useFormik } from 'formik';
import { useCallback, useEffect, useMemo } from 'react';
import { useToastModal } from '@/contexts/ToastModalContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import * as Yup from 'yup';
import s from './ContactForm.module.css';
import { useTranslations } from 'next-intl';

interface FormValues {
  names: string;
  maritalStatus: string;
  profession: string;
  email: string;
  budget: string;
  phone: string;
  contact: string;
  checked: boolean;
  hp: string;
}

const ContactForm = () => {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const { showToast } = useToastModal();
  const t = useTranslations('form');
  const tDataset = useTranslations('dataset');
  const phoneRegExp =
    /^((\+[1-9]{1,4}[ \-]*)|(\([0-9]{2,3}\)[ \-]*)|([0-9]{2,4})[ \-]*)*?[0-9]{3,4}?[ \-]*[0-9]{3,4}?$/;

  const { contactOptions, budgetOptions, maritalStatusOptions, professionOptions } = useMemo(
    () => getLocalizedDataset(tDataset),
    [tDataset]
  );

  useEffect(() => {
    const disableHubSpotFormCapture = () => {
      if (typeof window !== 'undefined') {
        try {
          Object.defineProperty(window, 'hbspt', {
            value: undefined,
            writable: false,
            configurable: true
          });
        } catch {
          console.log('HubSpot ya estaba bloqueado');
        }

        const originalAddEventListener = Element.prototype.addEventListener;
        Element.prototype.addEventListener = function (
          type: string,
          listener: EventListenerOrEventListenerObject,
          options?: boolean | AddEventListenerOptions
        ) {
          if (type === 'submit' && this.tagName === 'FORM') {
            const listenerStr = listener.toString();
            if (
              listenerStr.includes('hubspot') ||
              listenerStr.includes('hbspt') ||
              listenerStr.includes('hs-')
            ) {
              console.log('Blocked HubSpot form listener');
              return;
            }
          }
          return originalAddEventListener.call(this, type, listener, options);
        };

        console.log('HubSpot form capture disabled');
      }
    };

    disableHubSpotFormCapture();
  }, []);

  const formik = useFormik<FormValues>({
    initialValues: {
      names: '',
      maritalStatus: maritalStatusOptions[0].value,
      profession: professionOptions[0].value,
      email: '',
      budget: budgetOptions[0].value,
      phone: '',
      contact: contactOptions[0].value,
      checked: false,
      hp: ''
    },
    validationSchema: Yup.object().shape({
      names: Yup.string().required(t('validation.nameRequired')),
      maritalStatus: Yup.string().notOneOf(['default'], t('validation.selectOption')),
      profession: Yup.string().notOneOf(['default'], t('validation.selectOption')),
      email: Yup.string()
        .email(t('validation.emailInvalid'))
        .required(t('validation.emailRequired')),
      budget: Yup.string().notOneOf(['default'], t('validation.selectOption')),
      phone: Yup.string()
        .required(t('validation.phoneRequired'))
        .matches(phoneRegExp, t('validation.phoneInvalid')),
      contact: Yup.string().notOneOf(['default'], t('validation.selectOption')),
      checked: Yup.boolean().oneOf([true], t('validation.termsRequired'))
    }),
    onSubmit: (values) => {
      console.log('Form submission started');
      sendContact(values);
    }
  });

  const isSubmitDisabled = () => {
    return (
      formik.isSubmitting ||
      !formik.values.names ||
      !formik.values.email ||
      !formik.values.phone ||
      formik.values.maritalStatus === 'default' ||
      formik.values.profession === 'default' ||
      formik.values.budget === 'default' ||
      formik.values.contact === 'default' ||
      !formik.values.checked ||
      !!(formik.errors.names && formik.touched.names) ||
      !!(formik.errors.maritalStatus && formik.touched.maritalStatus) ||
      !!(formik.errors.profession && formik.touched.profession) ||
      !!(formik.errors.email && formik.touched.email) ||
      !!(formik.errors.budget && formik.touched.budget) ||
      !!(formik.errors.phone && formik.touched.phone) ||
      !!(formik.errors.contact && formik.touched.contact)
    );
  };

  const sendContact = useCallback(
    async (values: FormValues) => {
      try {
        let recaptchaToken: string | undefined;
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha('submit_form');
        } else {
          showToast(t('recaptchaError'), 'error');
          return;
        }
        const { names, maritalStatus, profession, email, phone, budget, contact, hp } = values;
        const campaign = getCampaignData();
        const selected_contact = contactOptions.find((item) => item.value === contact);
        const prospect =
          campaign === undefined
            ? {
                names,
                maritalStatus,
                profession,
                email,
                phone,
                budget,
                contact: selected_contact?.label,
                recaptchaToken,
                hp
              }
            : {
                names,
                maritalStatus,
                profession,
                email,
                phone,
                budget,
                contact: selected_contact?.label,
                ...campaign,
                recaptchaToken,
                hp
              };

        const { data } = await axios.post('/add-prospect/api', prospect);

        if (data.error === true) {
          if (data.code === 'VALIDATION_ERROR' && data.fields) {
            const fieldErrors = Object.values(data.fields).join(', ');
            throw new Error(t('validationFieldsError', { fields: fieldErrors }));
          }
          throw new Error(data.message || t('submitError'));
        }

        sendGTMEvent({
          event: '01_USER_SUBMIT_CONTACT_SUCCESS',
          value: '01_USER_SUBMIT_CONTACT_SUCCESS'
        });
        showToast(t('submitSuccess'), 'success');
        formik.resetForm();
      } catch {
        sendGTMEvent({
          event: '02_USER_SUBMIT_CONTACT_ERROR',
          value: '02_USER_SUBMIT_CONTACT_ERROR'
        });
        showToast(t('submitError'), 'error');
      }
    },
    [formik, executeRecaptcha, showToast, t, contactOptions]
  );

  return (
    <form
      className={s.container}
      onReset={formik.handleReset}
      onSubmit={formik.handleSubmit}
      autoComplete="on"
      data-hs-cf-bound="false"
      data-hubspot-ignore="true"
      data-no-hubspot="true">
      <input
        type="text"
        name="hp"
        value={formik.values.hp}
        onChange={formik.handleChange}
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={{
          position: 'absolute',
          left: '-10000px',
          top: 'auto',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      />
      <input
        type="text"
        name="cc-num"
        value=""
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        autoComplete="off"
        style={{ display: 'none' }}
      />
      <div>
        <FloatingLabel
          inputName="names"
          type="text"
          placeholder={t('namePlaceholder')}
          hasError={formik.touched.names}
          errorMessage={formik.errors.names}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          value={formik.values.names}
          autoComplete="name"
        />
        <FloatingLabel
          inputName="email"
          type="email"
          placeholder={t('emailPlaceholder')}
          hasError={formik.touched.email}
          errorMessage={formik.errors.email}
          handleChange={formik.handleChange}
          handleBlur={formik.handleBlur}
          value={formik.values.email}
          autoComplete="email"
        />
        <div className={s.phone_container}>
          <PhoneInput
            inputProps={{
              name: 'phone',
              required: true
            }}
            country={'mx'}
            value={formik.values.phone}
            onChange={(formattedValue) => {
              formik.setFieldValue('phone', formattedValue);
            }}
            onBlur={formik.handleBlur}
          />
          {formik.touched.phone && formik.errors.phone && (
            <div className={s.phone_container__error_message}>{formik.errors.phone}</div>
          )}
        </div>
      </div>
      <div>
        <div className={s.container__double}>
          <Select
            inputName="maritalStatus"
            options={maritalStatusOptions}
            value={formik.values.maritalStatus}
            handleChange={formik.handleChange}
            small
          />
          <Select
            inputName="profession"
            options={professionOptions}
            value={formik.values.profession}
            handleChange={formik.handleChange}
            small
          />
        </div>
        <Select
          inputName="budget"
          options={budgetOptions}
          value={formik.values.budget}
          handleChange={formik.handleChange}
        />
        <Select
          inputName="contact"
          options={contactOptions}
          value={formik.values.contact}
          handleChange={formik.handleChange}
        />
      </div>
      <div className={s.container__bottom}>
        <Checkbox
          inputName="checked"
          handleChange={formik.handleChange}
          checked={formik.values.checked}
          hasError={!!(formik.errors.checked && formik.touched.checked)}
          errorMessage={formik.errors.checked}
        />
        <Button
          type="submit"
          label={t('submit')}
          disabled={isSubmitDisabled()}
          onClick={() => {
            formik.submitForm();
          }}
        />
      </div>
    </form>
  );
};

export default ContactForm;
